import 'core-js/stable';
import 'regenerator-runtime/runtime';
import { createApp } from 'vue'
import App from './App.vue'
import axios from 'axios'
import VueAxios from 'vue-axios'
import { createRouter, createWebHashHistory } from 'vue-router'
import routes from './router'
import { validateUUIDParam } from './router/guards'
import { initMixpanel, mixpanel } from '@/plugins/mixpanel'
import { initNewRelic } from '@/plugins/newrelic'
import { registerAnalyticsRouteTracking } from '@/router/analytics'
import apiPropertyFetcher from './apiPropertyFetcher'
import { validateAndLog } from '@/utils/validation'
import './assets/styles/index.css'

// Detect environment from URL
function getEnvironment() {
  const host = window.location.hostname

  if (host.includes('localhost') || host.includes('127.0.0.1') || host.includes('dev')) {
    return 'dev'
  } else if (host.includes('stage') || host.includes('staging') || host.includes('stg')) {
    return 'stage'
  } else {
    return 'prod'
  }
}

const env = getEnvironment()

// Initialise analytics SDKs.
// Each function handles its own guards (env var, GPC, token presence).
initMixpanel(env)
initNewRelic(env)

// See `public/index.html` for the embeddable snippet.
const appID = 'content-library'
const appElement = document.getElementById(appID)

async function getHomeRoute() {
  let homeRoute = {
    path: '/',
    name: 'Home',
  }

  // Setting the route using query parameters:
  // Node ID: Pass `cl_type=node` and `cl_item={node id}`
  // Node IDs: Pass `cl_type=prescribed` and `cl_items={node id 1}|{node id 2}|...`
  // Type and UUID: Pass `cl_type={content library type}` and `cl_uuid`
  const urlParams = new URLSearchParams(window.location.search)
  const initialQuery = Object.fromEntries(urlParams)

  if (typeof initialQuery.cl_type === 'undefined') {
    homeRoute.component = () => import('@/components/ContentCollection.vue')
    homeRoute.props = {
      uuid: appElement.getAttribute('data-id'),
    }
  }
  else if (initialQuery.cl_type === 'prescribed' && typeof initialQuery.cl_items !== 'undefined') {
    let items = []

    await Promise
      .all(
        initialQuery.cl_items.split('|').map(( nodeID ) => {
          const validation = validateAndLog('nodeId', nodeID)
          if (!validation.valid) {
            console.error('Invalid node ID:', nodeID)
            return null
          }
          return apiPropertyFetcher({ nodeID })
        })
      )
      .catch((error) => {
        if (process.env.NODE_ENV === 'development') {
          console.log('error: ', error)
        }
      })
      .then(( data ) => {
        items = data
          .filter((element) => typeof element === 'object')
          .map(({ uuid, contentLibraryType }) => {
            return {
              uuid,
              type: contentLibraryType,
            }
          })

        return data
      })

    if (items.length > 0) {
      homeRoute.component = () => import('@/components/ContentList.vue')
      homeRoute.props = {
        items,
      }
    }
  }
  else if (initialQuery.cl_type === 'node' && typeof initialQuery.cl_item !== 'undefined') {
    const validation = validateAndLog('nodeId', initialQuery.cl_item)
    if (!validation.valid) {
      homeRoute.component = () => import('@/components/ErrorPage.vue')
      homeRoute.props = {
        message: 'Invalid node ID provided.',
      }
      return homeRoute
    }

    await apiPropertyFetcher({ nodeID: initialQuery.cl_item })
      .then( ({ uuid, contentLibraryType }) => {
        routes.forEach((route) => {
          if (route.path.split('/')[1] === contentLibraryType) {
            homeRoute.component = route.component
          }
        })

        homeRoute.props = {
          uuid,
        }
      })
      .catch((error) => {
        if (process.env.NODE_ENV === 'development') {
          console.log('error: ', error)
        }
      })
  }
  // The use case where a type matches the Content Library type and a UUID is provided.
  else if (typeof initialQuery.cl_uuid !== 'undefined') {
    let uuid = initialQuery.cl_uuid
    let type = initialQuery.cl_type

    const validation = validateAndLog('uuid', uuid)
    if (!validation.valid) {
      homeRoute.component = () => import('@/components/ErrorPage.vue')
      homeRoute.props = {
        message: 'Invalid UUID provided.',
      }
      return homeRoute
    }

    routes.forEach((route) => {
      if (route.path.split('/')[1] === type) {
        homeRoute.component = route.component
      }
    })

    homeRoute.props = {
      uuid: uuid,
    }
  }

  if (typeof homeRoute.component === 'undefined') {
    homeRoute.component = () => import('@/components/ErrorMessage.vue')
    homeRoute.props = {
      message: 'Content could not be found.',
    }
  }

  return homeRoute
}

// We are using promises to parse the various entrypoints, but we need to wait
// on those in order to create the application.
const initializeApp = async () => {
  routes.push({
    path: '/error',
    name: 'Error',
    component: () => import('@/components/ErrorPage.vue'),
    props: true
  })

  routes.push(await getHomeRoute())

  const router = createRouter({
    history: createWebHashHistory(),
    routes
  })

  router.beforeEach(validateUUIDParam)

  // Single merged afterEach for all analytics route tracking
  registerAnalyticsRouteTracking(router)

  const rootProperties = {}
  const app = createApp(App, rootProperties)

  // Report Vue component errors to New Relic with component/route context.
  // Without this, Vue errors appear as generic UncaughtError in NR with no trace.
  app.config.errorHandler = (err, instance, info) => {
    if (window.newrelic) {
      window.newrelic.noticeError(err, {
        vueComponent: instance?.$options?.name || instance?.$options?.__name || 'Anonymous',
        vueLifecycleHook: info,
        routeName: instance?.$route?.name || 'unknown',
        routePath: instance?.$route?.path || 'unknown'
      })
    }
    console.error(`[Vue Error] ${info}:`, err)
  }

  // Report Vue warnings to New Relic during local development only.
  // warnHandler only fires in Vue's dev runtime — never in production builds.
  if (process.env.NODE_ENV !== 'production') {
    app.config.warnHandler = (msg, instance, trace) => {
      if (window.newrelic) {
        window.newrelic.noticeError(new Error(`[Vue Warning] ${msg}`), {
          vueComponent: instance?.$options?.name || instance?.$options?.__name || 'Anonymous',
          vueTrace: trace?.substring(0, 500)
        })
      }
    }
  }

  // Attach Mixpanel to global properties so components can call this.$mixpanel
  app.config.globalProperties.$mixpanel = mixpanel

  app
    .use(router)
    .use(VueAxios, axios, router)
    .mount(`#${appID}`)
}

initializeApp()
